'use client'

import Image from 'next/image'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { Button } from 'ui/src/components/Button/Button'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { ChartIcon } from 'ui/src/icons/ChartIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { tokens } from 'ui/src/theme/tokens.css'
import { SignUpForm } from '../SignUpForm/SignUpForm'
import productExampleImage from './egyptiska_pallen.png'
import { vintageShopLargeDialogContent } from '../vintageShopLargeDialog.css'
import { dateBox, dateBoxText, dateRow, hiddenOnMobile, priceBox, priceBoxesRow } from './HowToBuyDialog.css'

const RELEASE_DATES: Array<{ day: string; month: string }> = [
  { day: '15', month: 'april' },
  { day: '16', month: 'april' },
  { day: '17', month: 'april' },
  { day: '18', month: 'april' },
  { day: '19', month: 'april' },
  { day: '20', month: 'april' },
  { day: '21', month: 'april' },
]

type ExamplePriceLabelKey =
  | 'VINTAGE_SHOP_ORIGINAL_PRICE_LABEL'
  | 'VINTAGE_SHOP_CURRENT_VALUE_LABEL'
  | 'VINTAGE_SHOP_OUR_PRICE_LABEL'

type PriceExample = {
  labelKey: ExamplePriceLabelKey
  amount: string
  trendPercentLabel?: string
}

const EXAMPLE_PRICES: Array<PriceExample> = [
  { labelKey: 'VINTAGE_SHOP_ORIGINAL_PRICE_LABEL', amount: '160 kr' },
  {
    labelKey: 'VINTAGE_SHOP_CURRENT_VALUE_LABEL',
    amount: '15 000 kr',
    trendPercentLabel: '+9 275%',
  },
  { labelKey: 'VINTAGE_SHOP_OUR_PRICE_LABEL', amount: '160 kr' },
]

type HowToBuyDialogProps = {
  buttonLabel?: string
}

export function HowToBuyDialog({ buttonLabel }: HowToBuyDialogProps) {
  const { t } = useTranslation(['vintageShop', 'common'])

  return (
    <LargeDialog.Root>
      <Dialog.Trigger asChild>
        <Button variant="primary">{buttonLabel}</Button>
      </Dialog.Trigger>
      <LargeDialog.Content className={vintageShopLargeDialogContent}>
        <LargeDialog.CloseIconButton aria-label={t('common:DIALOG_CLOSE')} />
        <div className={yStack({ gap: 'xxl' })}>
          <div className={yStack({ gap: 'lg' })}>
            <div>
              <LargeDialog.Heading className={sprinkles({ marginTop: 'xl' })}>
                {t('VINTAGE_SHOP_HOW_TO_BUY_DIALOG_HEADING')}
              </LargeDialog.Heading>
              <Text color="textSecondary" size={{ _: 'md', md: 'xl' }}>
                {t('VINTAGE_SHOP_HOW_TO_BUY_DIALOG_INTRO')}
              </Text>
            </div>

            <ul className={dateRow} aria-label={t('VINTAGE_SHOP_HOW_TO_BUY_DIALOG_DATES_LABEL')}>
              {RELEASE_DATES.map((item) => (
                <li key={item.day} className={dateBox}>
                  <Text className={dateBoxText} size={{ _: 'md', md: 'xl' }}>
                    {item.day}
                  </Text>
                  <Text className={dateBoxText} size={{ _: 'xs', md: 'md' }} color="textTranslucentSecondary">
                    {item.month}
                  </Text>
                </li>
              ))}
            </ul>
          </div>

          <div className={yStack({ gap: 'lg' })}>
            <div>
              <Heading as="h3" variant={{ _: 'standard.18', md: 'standard.24' }}>
                {t('VINTAGE_SHOP_HOW_TO_BUY_DIALOG_PRICE_SECTION_HEADING')}
              </Heading>
              <Text color="textSecondary" size={{ _: 'md', md: 'xl' }}>
                {t('VINTAGE_SHOP_HOW_TO_BUY_DIALOG_PRICE_SECTION_BODY')}
              </Text>
            </div>

            <div className={priceBoxesRow}>
              {EXAMPLE_PRICES.map((item, index) => (
                <div key={item.labelKey} className={clsx(priceBox, index === 0 && hiddenOnMobile)}>
                  <div className={xStack({ gap: 'xs', alignItems: 'flex-end' })}>
                    {item.trendPercentLabel ? (
                      <>
                        <Text className={sprinkles({ color: 'signalGreenElement' })} size="md">
                          {item.trendPercentLabel}
                        </Text>
                        <ChartIcon color={tokens.colors.signal.green.element} />
                      </>
                    ) : null}
                  </div>
                  <Image src={productExampleImage} alt="" width={85} height={85} sizes="85px" />
                  <div>
                    <Text size="md" color="textSecondary" align="center">
                      {t(item.labelKey)}
                    </Text>
                    <Text size={{ _: 'lg', md: 'xl' }} align="center">
                      {item.amount}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={yStack({ gap: 'lg' })}>
            <div>
              <Heading as="h3" variant={{ _: 'standard.18', md: 'standard.24' }}>
                {t('VINTAGE_SHOP_HOW_TO_BUY_DIALOG_SIGN_UP_HEADING')}
              </Heading>
              <Text color="textSecondary" size={{ _: 'md', md: 'xl' }}>
                {t('VINTAGE_SHOP_HOW_TO_BUY_DIALOG_SIGN_UP_BODY')}
              </Text>
            </div>
            <SignUpForm />
          </div>
        </div>
      </LargeDialog.Content>
    </LargeDialog.Root>
  )
}
